import { useUpdatePetitionMutation } from "@codegen/index";
import { FilingType, UpdatePetitionByIdInput } from "@codegen/schema";
import { FormContainer, OnboardingContainer } from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useBeneficiaryWorkflowContext } from "@pages/onboarding/workflows/provider";
import { useDebounceCallback } from "@react-hook/debounce";
import { useLogError } from "@utils/error";
import React, { useCallback, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

export const FilingTypeFormSchema = z.object({
  filingType: z.string().min(1, "Please select a filing type"),
});

export type FilingTypeFormType = z.infer<typeof FilingTypeFormSchema>;
const changeOfStatusDescription = [
  "This process is for individuals already in the United States in a valid nonimmigrant status.",
  "It allows them to switch from one nonimmigrant visa category (e.g. H-1B visa to O-1 visa)  to another without leaving the country.",
  "If approved, the person's status changes seamlessly.",
  "Once you leave the U.S., you will have to apply for a visa stamp to enter the country (unless you're Canadian! Canadians are visa-exempt.).",
];
const consularProcessDescription = [
  "This is typically for individuals outside the United States.",
  "Do you have upcoming travel? If you are currently in the U.S. but are planning to leave the U.S. during your visa process, you should select this option.",
  "The applicant must schedule an interview at a U.S. consulate in their home country or country of residence to apply for a visa.",
  "If you have been issued a U.S. visa recently, your consulate may waive the interview requirement, but you will need to send your passport for processing.",
  "If approved, you will receive a visa stamp in your passport, allowing you to enter the U.S. in the new visa category.",
];

const descriptionArray =
  consularProcessDescription.length >= changeOfStatusDescription.length
    ? consularProcessDescription
    : changeOfStatusDescription;

export const FilingTypeForm: React.FC<OnboardingPageType> = ({
  petition,
  onSubmit,
  autoSave,
  onBack,
  exhibit,
  onSkipForNow,
  showSkipForNow,
  hideExitButton,
}) => {
  const { refetchPetitions } = useBeneficiaryWorkflowContext();
  const methods = useForm({
    defaultValues: {
      filingType: petition.filingType || "",
    },
    resolver: zodResolver(FilingTypeFormSchema),
    mode: "onChange",
  });
  const { register, control, setError } = methods;
  const { isReadOnly } = useReadOnlyMode();
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const watchedValues = useWatch({
    control,
  });

  const [updateFilingType, { loading: saving }] = useUpdatePetitionMutation();
  const logError = useLogError();

  const performMutation = useCallback(
    async (data: FilingTypeFormType) => {
      const { errors: updateFilingTypeError } = await updateFilingType({
        variables: {
          input: {
            id: petition.id,
            petitionPatch: {
              filingType: data.filingType as FilingType,
            },
          } as UpdatePetitionByIdInput,
        },
      });

      if (updateFilingTypeError != null) {
        throw updateFilingTypeError;
      }

      // Refetch petitions to ensure fresh data
      await refetchPetitions();
    },
    [updateFilingType, petition.id, refetchPetitions]
  );

  const handleSave = useCallback(
    async (data: FilingTypeFormType) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          await performMutation(data);
          setStatus("success");
          autoSave(data);
        } catch (exception) {
          setStatus("error");
          if (exception instanceof Error) {
            setError("root.serverError", {
              type: exception.name,
              message: exception.message,
            });
          }
          logError(exception, {
            message: "Error saving filing type",
            data,
          });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [performMutation, autoSave, setError, logError]
  );

  const debouncedSave = useDebounceCallback(handleSave, 300);

  React.useEffect(() => {
    // Only call debouncedSave if the form data is valid
    const parseResult = FilingTypeFormSchema.safeParse(watchedValues);
    if (parseResult.success) {
      debouncedSave(parseResult.data);
    }
  }, [watchedValues, debouncedSave]);

  const handleSubmit = useCallback(
    async (data: FilingTypeFormType) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await performMutation(data);
        setStatus("success");
        onSubmit(data);
      } catch (exception) {
        setStatus("error");
        if (exception instanceof Error) {
          setError("root.serverError", {
            type: exception.name,
            message: exception.message,
          });
        }
        logError(exception, {
          message: "Error submitting filing type",
          data,
        });
      }
    },
    [performMutation, onSubmit, setError, logError]
  );

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={`Filing Type`}
        subtitle="The main difference between change of status and consular processing for U.S. work visas lies in where and how your visa is obtained."
        description="Please select a filing type"
        progress={0}
        onSubmit={
          methods.handleSubmit(handleSubmit) as (
            data?: unknown
          ) => void | Promise<void>
        }
        exhibit={exhibit}
        name={`filing-type-form`}
        onBack={onBack}
        onSkipForNow={onSkipForNow}
        isLoading={status === "loading" || saving}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem saving the filing type. Please try again."
        >
          <table className="shadow-sm rounded text-sm mt-3 w-full text-left rtl:text-right text-gray-500 dark:text-gray-400 bg-white">
            <thead className=" text-gray-700 uppercase dark:bg-gray-700 dark:text-gray-400 bg-gray-50">
              <tr className="">
                <th className="p-3 border">
                  <label
                    className="flex items-center gap-2"
                    htmlFor="changeOfStatusRadio"
                  >
                    <input
                      type="radio"
                      id="changeOfStatusRadio"
                      value={FilingType.ChangeOfStatus}
                      {...register("filingType")}
                      disabled={isReadOnly}
                      title="Change of Status filing type"
                    />
                    Change of Status
                  </label>
                </th>
                <th className="p-3 border">
                  <label
                    className="flex items-center gap-2"
                    htmlFor="consularProcessingRadio"
                  >
                    <input
                      type="radio"
                      id="consularProcessingRadio"
                      value={FilingType.ConsularProcessing}
                      {...register("filingType")}
                      disabled={isReadOnly}
                      title="Consular Processing filing type"
                    />
                    Consular Processing
                  </label>
                </th>
              </tr>
            </thead>
            <tbody className="">
              {descriptionArray.map((_, index) => (
                <tr key={`file-type-${index}`}>
                  <td className="p-3 border align-top">
                    {changeOfStatusDescription[index]}
                  </td>
                  <td className="p-3 border align-top">
                    {consularProcessDescription[index]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export default FilingTypeForm;
